import { type DataNeededId, type SuretyAccountId } from "@inrev/common";
import { useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import {
	dataNeededQueryKey,
	decisionsQueryKey,
	resolvedDataNeededQueryKey,
} from "../decisioning.keys";
import { type DataNeeded } from "../types";
import { invalidateListWhenIdle, useOptimisticListMutation } from "../useOptimisticListMutation";

/** Soft-deletes a data-needed item (open or resolved). */
export const useDeleteDataNeeded = (accountId: SuretyAccountId) => {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useOptimisticListMutation<DataNeeded, DataNeededId>({
		queryKey: dataNeededQueryKey(accountId),
		request: async (id) => {
			await _delete(`/v2/admin/surety/accounts/${accountId}/data-needed/${id}`, undefined, "none");
		},
		apply: (items, id) => items.filter((item) => item.id !== id),
		affectedIds: (id) => [id],
		failureMessage: () => "Couldn't delete the request.",
		reconcile: (items, _response, id) => {
			queryClient.setQueryData<DataNeeded[]>(
				resolvedDataNeededQueryKey(accountId),
				(resolved) => resolved && resolved.filter((item) => item.id !== id),
			);
			void invalidateListWhenIdle(queryClient, decisionsQueryKey(accountId));
			return items;
		},
	});

	return {
		deleteDataNeeded: mutation.mutate,
	};
};
